import { FC, useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Grid,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Divider,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import CodeIcon from '@mui/icons-material/Code'
import { FormElement, Variable, MobileListItem, ContextMenuAction } from '@mobileTypes'
import { useMobileListItems } from '@mobileHooks'
import { VariableAutocompleteWithCreate } from '@mobileComponents/Variables'
import { VectorIcon, IconPickerField } from '@microservices/wiskey-react-components'
import { ICON_REGISTRY } from '@mobileConstants'
import { ColorPickerField } from '@components/ColorPicker'
import { ScriptValueDialog, variablesToFieldsInfo } from '@microservices/wiskey-react-components'
import { InternalIdInput } from '@library'

interface EmbeddedListConfigDialogProps {
  open: boolean
  initialData?: Partial<FormElement>
  variables: Variable[]
  onClose: () => void
  onSave: (config: {
    internalId?: string
    embeddedListItemId?: string
    embeddedListConfig?: Partial<MobileListItem>
    selectedIdsVariable?: string
    showCheckboxes?: boolean
    itemIdField?: string
    contextMenuActions?: ContextMenuAction[]
    listHeader?: string
    listSubheader?: string
    listSubheaderJs?: string
    selectAllEnabled?: boolean
    selectAllText?: string
    variableBindings?: Record<string, string>
    readOnlyIfJs?: string
  }) => void
  onCreateVariable?: (name: string, isGlobal: boolean, valueType?: string, variableType?: string) => void
}

interface ContextMenuActionFormData {
  id: string
  title: string
  icon?: VectorIcon | null
  actionMeta?: any
}

export const EmbeddedListConfigDialog: FC<EmbeddedListConfigDialogProps> = ({
  open,
  initialData,
  variables,
  onClose,
  onSave,
  onCreateVariable,
}) => {
  const { listItems } = useMobileListItems()

  const [internalId, setInternalId] = useState<string>(initialData?.internalId ?? '')
  const [selectedListItemId, setSelectedListItemId] = useState<string | null>(
    initialData?.embeddedListItemId || null
  )
  const [selectedIdsVariable, setSelectedIdsVariable] = useState<string>(
    initialData?.selectedIdsVariable || ''
  )
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(
    initialData?.showCheckboxes !== undefined ? initialData.showCheckboxes : true
  )
  const [itemIdField, setItemIdField] = useState<string>(
    initialData?.itemIdField || '_id'
  )
  const [contextMenuActions, setContextMenuActions] = useState<ContextMenuActionFormData[]>(
    initialData?.contextMenuActions?.map(action => ({
      id: action.id,
      title: action.title,
      icon: action.icon as VectorIcon | null,
      actionMeta: action.actionMeta,
    })) || []
  )
  const [editingActionIndex, setEditingActionIndex] = useState<number | null>(null)

  // Header and subheader settings
  const [listHeader, setListHeader] = useState<string>(initialData?.listHeader || '')
  const [listSubheader, setListSubheader] = useState<string>(initialData?.listSubheader || '')
  const [useSubheaderJs, setUseSubheaderJs] = useState<boolean>(!!initialData?.listSubheaderJs)
  const [listSubheaderJs, setListSubheaderJs] = useState<string>(initialData?.listSubheaderJs || '')
  const [showSubheaderJsEditor, setShowSubheaderJsEditor] = useState(false)
  const [selectAllEnabled, setSelectAllEnabled] = useState<boolean>(
    initialData?.selectAllEnabled !== undefined ? initialData.selectAllEnabled : false
  )
  const [selectAllText, setSelectAllText] = useState<string>(
    initialData?.selectAllText || 'Select All'
  )

  // Variable bindings for prefilled variables
  const [variableBindings, setVariableBindings] = useState<Record<string, string>>(
    initialData?.variableBindings || {}
  )

  // Read-only when JS returns true (e.g. when order state is completed)
  const [readOnlyIfJs, setReadOnlyIfJs] = useState<string>(initialData?.readOnlyIfJs || '')
  const [showReadOnlyIfJsEditor, setShowReadOnlyIfJsEditor] = useState(false)

  const subheaderJsScriptVariables = useMemo(
    () => [
      { name: 'items', type: 'Array<object>', description: 'All items in the list' },
      { name: 'checkedItems', type: 'Array<object>', description: 'Items that are checked' },
      { name: 'uncheckedItems', type: 'Array<object>', description: 'Items that are not checked' },
    ],
    []
  )
  const readOnlyIfJsScriptVariables = useMemo(
    () => [
      { name: 'item', type: 'object', description: 'Object from route (e.g. order)' },
      { name: 'formData', type: 'object', description: 'Form field values' },
      { name: 'localVariables', type: 'object', description: 'Form variables' },
      { name: 'globalVariables', type: 'object', description: 'Global variables' },
    ],
    []
  )

  useEffect(() => {
    if (open) {
      setInternalId(initialData?.internalId ?? '')
    }
  }, [open, initialData?.internalId])

  const selectedListItem = useMemo(() => {
    if (!selectedListItemId) return null
    return listItems.find(item => item.internalId === selectedListItemId) || null
  }, [selectedListItemId, listItems])

  // Get prefilled variables from the selected list item
  const prefilledVariables = useMemo(() => {
    return selectedListItem?.prefilledVariables || []
  }, [selectedListItem])

  const availableVariables = useMemo(() => {
    return variables.filter(v => v.variableType === 'multiple' && v.valueType === "boolean")
  }, [variables])

  const handleSave = () => {
    if (!selectedListItemId && !initialData?.embeddedListConfig) {
      // Можно добавить валидацию
      return
    }

    const config: {
      internalId?: string
      embeddedListItemId?: string
      embeddedListConfig?: Partial<MobileListItem>
      selectedIdsVariable?: string
      showCheckboxes?: boolean
      itemIdField?: string
      contextMenuActions?: ContextMenuAction[]
      listHeader?: string
      listSubheader?: string
      listSubheaderJs?: string
      selectAllEnabled?: boolean
      selectAllText?: string
      variableBindings?: Record<string, string>
      readOnlyIfJs?: string
    } = {
      showCheckboxes,
      itemIdField,
    }

    if (internalId.trim()) {
      config.internalId = internalId.trim()
    }

    if (selectedListItemId) {
      config.embeddedListItemId = selectedListItemId
    } else if (initialData?.embeddedListConfig) {
      config.embeddedListConfig = initialData.embeddedListConfig
    }

    if (selectedIdsVariable) {
      config.selectedIdsVariable = selectedIdsVariable
    }

    if (contextMenuActions.length > 0) {
      config.contextMenuActions = contextMenuActions.map(action => ({
        id: action.id,
        title: action.title,
        icon: action.icon,
        actionMeta: action.actionMeta,
      }))
    }

    // Header and subheader settings
    if (listHeader) {
      config.listHeader = listHeader
    }
    if (listSubheader) {
      config.listSubheader = listSubheader
    }
    if (useSubheaderJs && listSubheaderJs) {
      config.listSubheaderJs = listSubheaderJs
    }
    config.selectAllEnabled = selectAllEnabled
    if (selectAllEnabled && selectAllText) {
      config.selectAllText = selectAllText
    }

    // Variable bindings
    if (Object.keys(variableBindings).length > 0) {
      config.variableBindings = variableBindings
    }

    if (readOnlyIfJs) {
      config.readOnlyIfJs = readOnlyIfJs
    }

    onSave(config)
  }

  const handleAddAction = () => {
    const newAction: ContextMenuActionFormData = {
      id: `action_${Date.now()}`,
      title: '',
      icon: null,
    }
    setContextMenuActions([...contextMenuActions, newAction])
    setEditingActionIndex(contextMenuActions.length)
  }

  const handleDeleteAction = (index: number) => {
    const newActions = contextMenuActions.filter((_, i) => i !== index)
    setContextMenuActions(newActions)
    if (editingActionIndex === index) {
      setEditingActionIndex(null)
    } else if (editingActionIndex !== null && editingActionIndex > index) {
      setEditingActionIndex(editingActionIndex - 1)
    }
  }

  const handleUpdateAction = (index: number, updates: Partial<ContextMenuActionFormData>) => {
    const newActions = [...contextMenuActions]
    newActions[index] = { ...newActions[index], ...updates }
    setContextMenuActions(newActions)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configure Embedded List</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <InternalIdInput
              value={internalId}
              onChange={setInternalId}
              fullWidth
              label="Internal ID"
              helperText="Unique ID for references and event flows"
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={listItems}
              getOptionLabel={(option) => option.name}
              value={selectedListItem}
              onChange={(_, newValue) => {
                setSelectedListItemId(newValue?.internalId || null)
                // Reset variable bindings when list item changes
                setVariableBindings({})
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select List Item Configuration"
                  helperText="Choose an existing list item configuration"
                  required
                />
              )}
            />
          </Grid>

          {/* Variable Bindings */}
          {prefilledVariables.length > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Variable Bindings
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Map List Item prefilled variables to Form variables
                </Typography>
              </Grid>

              {prefilledVariables.map((prefilledVar) => {
                const listItemVariable = selectedListItem?.variables?.find(v => v.internalId === prefilledVar.internalId)
                const isArrayType = listItemVariable?.variableType === 'multiple'
                const valueType = listItemVariable?.valueType

                const compatibleVariables = variables.filter(v => {
                  if (isArrayType) {
                    return v.variableType === 'multiple'
                  }
                  return v.valueType === valueType || !v.valueType || !valueType
                })

                return (
                  <Grid item xs={12} key={prefilledVar.internalId}>
                    <Autocomplete
                      options={compatibleVariables}
                      getOptionLabel={(option) => option.internalId}
                      value={compatibleVariables.find(v => v.internalId === variableBindings[prefilledVar.internalId]) || null}
                      onChange={(_, newValue) => {
                        setVariableBindings(prev => ({
                          ...prev,
                          [prefilledVar.internalId]: newValue?.internalId || '',
                        }))
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${prefilledVar.internalId}${prefilledVar.required ? ' *' : ''}`}
                          helperText={listItemVariable ? `Type: ${isArrayType ? 'Array' : 'Single'} • ${valueType || 'any'}` : ''}
                        />
                      )}
                    />
                  </Grid>
                )
              })}
            </>
          )}

          {/* Header and Summary Settings */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              Header & Summary
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="List Header"
              value={listHeader}
              onChange={(e) => setListHeader(e.target.value)}
              helperText="Optional header text"
              placeholder="Enter header text..."
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useSubheaderJs}
                  onChange={(e) => {
                    setUseSubheaderJs(e.target.checked)
                    if (!e.target.checked) {
                      setListSubheaderJs('')
                    } else {
                      setListSubheader('')
                    }
                  }}
                />
              }
              label="Use JavaScript for Subheader"
            />
          </Grid>

          {!useSubheaderJs && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subheader"
                value={listSubheader}
                onChange={(e) => setListSubheader(e.target.value)}
                helperText="Static subheader text"
                placeholder="Enter static subheader..."
              />
            </Grid>
          )}

          {useSubheaderJs && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowSubheaderJsEditor(true)}
                startIcon={<CodeIcon />}
              >
                {listSubheaderJs ? 'Edit JavaScript Expression' : 'Add JavaScript Expression'}
              </Button>
              {listSubheaderJs && (
                <Box mt={1} p={1} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {listSubheaderJs.length > 100 ? listSubheaderJs.substring(0, 100) + '...' : listSubheaderJs}
                  </Typography>
                </Box>
              )}
            </Grid>
          )}

          {/* Selection Settings */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              Selection Settings
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Read-only when (JS)"
              value={readOnlyIfJs}
              onChange={(e) => setReadOnlyIfJs(e.target.value)}
              helperText="When JS returns true, list is read-only (no checkboxes, no context menu). Context: item, formData, localVariables, globalVariables"
              placeholder="e.g. localVariables.order?.order_state_ref?._id === '...'"
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={() => setShowReadOnlyIfJsEditor(true)} title="Edit JS">
                    <CodeIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Item ID Field"
              value={itemIdField}
              onChange={(e) => setItemIdField(e.target.value)}
              helperText="Field name for item identification (default: _id)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCheckboxes}
                  onChange={(e) => setShowCheckboxes(e.target.checked)}
                />
              }
              label="Show Checkboxes"
            />
          </Grid>

          {showCheckboxes && (
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectAllEnabled}
                    onChange={(e) => setSelectAllEnabled(e.target.checked)}
                  />
                }
                label="Enable 'Select All'"
              />
            </Grid>
          )}

          {showCheckboxes && selectAllEnabled && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Select All Text"
                value={selectAllText}
                onChange={(e) => setSelectAllText(e.target.value)}
                helperText="Text for 'Select All' toggle"
                placeholder="Select All"
              />
            </Grid>
          )}

          {showCheckboxes && <Grid item xs={12}>
            <VariableAutocompleteWithCreate
              options={availableVariables}
              valueInternalId={selectedIdsVariable}
              onChangeInternalId={setSelectedIdsVariable}
              label="Selected IDs Variable"
              helperText="Variable to store selected item IDs"
              onCreateVariable={onCreateVariable}
              createDialogTitle="Create Variable for Selected IDs"
              createDialogDescription="Create a variable to store the selected item IDs from the embedded list."
              defaultScope="local"
              showScopeSelector
              defaultVariableType="multiple"
              defaultValueType="boolean"
            />
          </Grid>}

          {/* Context Menu Actions */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Context Menu Actions</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddAction}
              >
                Add Action
              </Button>
            </Box>

            {contextMenuActions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No actions configured. Click "Add Action" to add one.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {contextMenuActions.map((action, index) => (
                  <Box
                    key={action.id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: editingActionIndex === index ? 'action.selected' : 'background.paper',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {action.title || `Action ${index + 1}`}
                      </Typography>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => setEditingActionIndex(editingActionIndex === index ? null : index)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteAction(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {editingActionIndex === index && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Action ID"
                            value={action.id}
                            onChange={(e) => handleUpdateAction(index, { id: e.target.value })}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Action Title"
                            value={action.title}
                            onChange={(e) => handleUpdateAction(index, { title: e.target.value })}
                            required
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <IconPickerField
                            value={action.icon || null}
                            onChange={(newIcon) => handleUpdateAction(index, { icon: newIcon })}
                            iconRegistry={ICON_REGISTRY}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>

      {/* Script Value Dialog for Subheader JS */}
      {showSubheaderJsEditor && (
        <ScriptValueDialog
          isShow
          value={listSubheaderJs}
          title="Edit Subheader JavaScript"
          onClose={() => setShowSubheaderJsEditor(false)}
          onSave={(code: string) => {
            setListSubheaderJs(code)
            setShowSubheaderJsEditor(false)
          }}
          language="js"
          width={800}
          variables={subheaderJsScriptVariables}
          fieldsInfo={variablesToFieldsInfo(subheaderJsScriptVariables)}
        />
      )}

      {/* Script Value Dialog for Read-only If JS */}
      {showReadOnlyIfJsEditor && (
        <ScriptValueDialog
          isShow
          value={readOnlyIfJs}
          title="Edit Read-only If JavaScript"
          onClose={() => setShowReadOnlyIfJsEditor(false)}
          onSave={(code: string) => {
            setReadOnlyIfJs(code)
            setShowReadOnlyIfJsEditor(false)
          }}
          language="js"
          width={800}
          variables={readOnlyIfJsScriptVariables}
          fieldsInfo={variablesToFieldsInfo(readOnlyIfJsScriptVariables)}
          examples={[
            {
              title: 'When order state is completed',
              code: "return localVariables.order?.order_state_ref?._id === '65780621dcc5ff7ee73eaee9'",
            },
          ]}
        />
      )}
    </Dialog>
  )
}
